import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { ImagePlus, Loader2, Paintbrush, RotateCcw, Trash2, UploadCloud, CheckCircle2, ClipboardList } from 'lucide-react';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const OVERLAY_CLASS = {
  soft: 'bg-gradient-to-r from-[#0B1E38]/70 via-[#0B1E38]/40 to-[#0B1E38]/20',
  medium: 'bg-gradient-to-r from-[#0B1E38]/85 via-[#0B1E38]/60 to-[#0B1E38]/35',
  strong: 'bg-gradient-to-r from-[#0B1E38]/95 via-[#0B1E38]/78 to-[#0B1E38]/55',
};
export const FOCAL_POS = { left: 'left center', center: 'center center', right: 'right center' };

export const companyInitials = (name) =>
  (name || '')
    .split(/\s+/)
    .filter((w) => w.length > 2 || /^[A-ZÁÉÍÓÚÑ]/.test(w))
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || 'CT';

// Miniature reproduction of the client dashboard hero so admins preview the
// real overlay treatment (welcome, name, date, chips, logo) before publishing.
const HeroPreview = ({ bannerUrl, focal = 'center', overlay = 'medium', companyName, logoUrl, testId }) => {
  const { t, i18n } = useTranslation();
  const dateStr = new Date().toLocaleDateString(i18n.language?.startsWith('es') ? 'es' : 'en',
    { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  return (
    <div className="relative overflow-hidden rounded-xl h-48 text-white shadow-soft" data-testid={testId}>
      {bannerUrl ? (
        <>
          <img src={bannerUrl} alt="" aria-hidden="true"
               className="absolute inset-0 h-full w-full object-cover"
               style={{ objectPosition: FOCAL_POS[focal] || 'center center' }} />
          <div className={`absolute inset-0 ${OVERLAY_CLASS[overlay] || OVERLAY_CLASS.medium}`} aria-hidden="true" />
        </>
      ) : (
        <>
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B1E38] via-[#11304F] to-[#1C4B7A]" aria-hidden="true" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_40%,rgba(255,255,255,0.14),transparent_55%)]" aria-hidden="true" />
        </>
      )}
      <div className="relative h-full px-5 py-4 flex items-center gap-4">
        <div className="flex-1 min-w-0">
          <p className="text-white/60 text-[10px] uppercase tracking-[0.18em] truncate">{dateStr}</p>
          <p className="font-display text-lg font-bold mt-0.5 leading-tight">
            {t('branding.previewGreeting', { defaultValue: 'Bienvenido/a' })}
          </p>
          <p className="text-white/90 text-xs font-semibold mt-0.5 leading-snug break-words line-clamp-2">{companyName}</p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            <span className="inline-flex items-center gap-1 text-[10px] bg-white/10 border border-white/20 rounded-full px-2 py-0.5 text-white/90">
              <ClipboardList className="h-2.5 w-2.5" /> {t('branding.chipRequests', { defaultValue: 'Solicitudes pendientes' })}
            </span>
            <span className="inline-flex items-center gap-1 text-[10px] bg-white/10 border border-white/20 rounded-full px-2 py-0.5 text-white/90">
              <CheckCircle2 className="h-2.5 w-2.5" /> {t('branding.chipReports', { defaultValue: 'Informes liberados' })}
            </span>
          </div>
        </div>
        {logoUrl ? (
          <div className="h-16 w-16 shrink-0 rounded-full bg-white/95 ring-2 ring-white/15 flex items-center justify-center p-1.5">
            <img src={logoUrl} alt="" className="h-full w-full object-contain" />
          </div>
        ) : (
          <div className="h-16 w-16 shrink-0 rounded-full bg-white/10 ring-2 ring-white/15 flex items-center justify-center">
            <span className="font-display text-xl font-bold text-white/90">{companyInitials(companyName)}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const SegRow = ({ label, value, options, onChange, disabled, testPrefix }) => (
  <div className="flex items-center gap-2 flex-wrap">
    <span className="text-xs font-medium text-muted-foreground w-32">{label}</span>
    <div className="flex gap-1.5">
      {options.map((o) => (
        <button key={o.value} type="button" disabled={disabled}
                onClick={() => onChange(o.value)}
                data-testid={`${testPrefix}-${o.value}`}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  value === o.value ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-muted/50'
                } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
          {o.label}
        </button>
      ))}
    </div>
  </div>
);

export const ClientBrandingSection = ({ companyId, company, readOnly }) => {
  const { t } = useTranslation();
  const [meta, setMeta] = useState(null);
  const [auditItems, setAuditItems] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [logoOpen, setLogoOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const logoFileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [m, a] = await Promise.all([
        api.get(`/branding/admin/companies/${companyId}/branding`).then((r) => r.data),
        api.get(`/branding/admin/companies/${companyId}/branding/audit`).then((r) => r.data).catch(() => ({ items: [] })),
      ]);
      setMeta(m);
      setAuditItems(a.items || []);
    } catch { /* section stays hidden-safe */ }
  }, [companyId]);
  useEffect(() => { load(); }, [load]);

  if (!meta) return null;
  const published = meta.published;
  const draft = meta.draft;
  const cb = meta.custom_banner || {};
  const publishedUrl = published ? `${API_BASE}/branding/banner/${companyId}?v=${published.ref}` : null;
  const draftUrl = draft ? `${API_BASE}/branding/banner/${companyId}/draft?v=${draft.ref}` : null;
  const logoDraft = meta.logo_draft;
  const logoPublished = meta.logo_published;
  const logoDraftUrl = logoDraft ? `${API_BASE}/branding/logo-draft/${companyId}?v=${logoDraft.ref}` : null;

  const onPickLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('branding.errTooLarge', { defaultValue: 'La imagen supera el máximo permitido de 5 MB.' }));
      return;
    }
    if (!/\.(png|jpe?g|webp)$/i.test(file.name)) {
      toast.error(t('branding.errFormat', { defaultValue: 'Formato no compatible. Utiliza PNG, JPG o WEBP.' }));
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/branding/admin/companies/${companyId}/branding/logo`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
      toast.success(t('branding.logoUploaded', { defaultValue: 'Logo cargado. Revisa la vista previa antes de publicar.' }));
      setLogoOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setUploading(false); }
  };
  const doPublishLogo = async () => {
    setBusy(true);
    try {
      await api.post(`/branding/admin/companies/${companyId}/branding/logo/publish`);
      toast.success(t('branding.logoPublishedToast', { defaultValue: 'Logo publicado. Actualizando…' }));
      setTimeout(() => window.location.reload(), 900);
    } catch (err) { toast.error(formatApiError(err)); setBusy(false); }
  };
  const doDiscardLogo = async () => {
    setBusy(true);
    try {
      await api.post(`/branding/admin/companies/${companyId}/branding/logo/discard-draft`);
      toast.success(t('branding.discardedToast', { defaultValue: 'Borrador descartado.' }));
      await load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const doRestoreLogo = async () => {
    if (!window.confirm(t('branding.restoreLogoConfirm', { defaultValue: '¿Revertir al logo anterior?' }))) return;
    setBusy(true);
    try {
      await api.post(`/branding/admin/companies/${companyId}/branding/logo/restore-previous`);
      toast.success(t('branding.logoRestoredToast', { defaultValue: 'Se restauró el logo anterior. Actualizando…' }));
      setTimeout(() => window.location.reload(), 900);
    } catch (err) { toast.error(formatApiError(err)); setBusy(false); }
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('branding.errTooLarge', { defaultValue: 'La imagen supera el máximo permitido de 5 MB.' }));
      return;
    }
    if (!/\.(png|jpe?g|webp)$/i.test(file.name)) {
      toast.error(t('branding.errFormat', { defaultValue: 'Formato no compatible. Utiliza PNG, JPG o WEBP.' }));
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/branding/admin/companies/${companyId}/branding/banner`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
      toast.success(t('branding.uploaded', { defaultValue: 'Banner cargado. Revisa la vista previa antes de publicar.' }));
      setUploadOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setUploading(false); }
  };

  const setSetting = async (patch) => {
    try {
      await api.patch(`/branding/admin/companies/${companyId}/branding/settings`, patch);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const doPublish = async () => {
    setBusy(true);
    try {
      await api.post(`/branding/admin/companies/${companyId}/branding/publish`);
      toast.success(t('branding.publishedToast', { defaultValue: 'Banner publicado.' }));
      await load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const doDiscard = async () => {
    setBusy(true);
    try {
      await api.post(`/branding/admin/companies/${companyId}/branding/discard-draft`);
      toast.success(t('branding.discardedToast', { defaultValue: 'Borrador descartado.' }));
      await load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const doRestore = async () => {
    if (!window.confirm(t('branding.restoreConfirm', { defaultValue: '¿Volver al diseño predeterminado del panel?' }))) return;
    setBusy(true);
    try {
      await api.post(`/branding/admin/companies/${companyId}/branding/restore-default`);
      toast.success(t('branding.restoredToast', { defaultValue: 'Se restauró el diseño predeterminado.' }));
      await load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const focalOpts = [
    { value: 'left', label: t('branding.focalLeft', { defaultValue: 'Izquierda' }) },
    { value: 'center', label: t('branding.focalCenter', { defaultValue: 'Centro' }) },
    { value: 'right', label: t('branding.focalRight', { defaultValue: 'Derecha' }) },
  ];
  const overlayOpts = [
    { value: 'soft', label: t('branding.overlaySoft', { defaultValue: 'Suave' }) },
    { value: 'medium', label: t('branding.overlayMedium', { defaultValue: 'Medio' }) },
    { value: 'strong', label: t('branding.overlayStrong', { defaultValue: 'Fuerte' }) },
  ];

  return (
    <Card className="p-6 shadow-card" data-testid="client-branding-section">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <h3 className="font-display font-bold text-lg tracking-tight inline-flex items-center gap-2">
            <Paintbrush className="h-4 w-4 text-primary" />
            {t('branding.title', { defaultValue: 'Identidad visual' })}
          </h3>
          <p className="text-sm text-muted-foreground">{t('branding.subtitle', { defaultValue: 'Logo y banner del panel del cliente — gestionado por Corporate Talent Hub.' })}</p>
          {readOnly && (
            <p className="text-xs text-warning mt-1" data-testid="branding-readonly-note">
              {t('branding.readOnly', { defaultValue: 'Solo lectura — la gestión de marca requiere rol HM Admin.' })}
            </p>
          )}
        </div>
        {!readOnly && (
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" onClick={() => setUploadOpen(true)} data-testid="branding-upload-btn" className="gap-1.5">
              <UploadCloud className="h-4 w-4" />
              {cb.enabled ? t('branding.replace', { defaultValue: 'Reemplazar banner' }) : t('branding.upload', { defaultValue: 'Subir banner' })}
            </Button>
            {cb.enabled && (
              <Button size="sm" variant="outline" onClick={doRestore} disabled={busy} data-testid="branding-restore-btn" className="gap-1.5">
                <RotateCcw className="h-4 w-4" /> {t('branding.restore', { defaultValue: 'Restaurar diseño predeterminado' })}
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div data-testid="branding-current-logo">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-2">
            {t('branding.currentLogo', { defaultValue: 'Logo actual' })}
          </p>
          {company?.logo_url ? (
            <div className="h-24 w-24 rounded-xl bg-white border border-border/60 shadow-soft flex items-center justify-center p-2">
              <img src={company.logo_url} alt={company?.name} className="h-full w-full object-contain" />
            </div>
          ) : (
            <div className="h-24 w-24 rounded-xl bg-muted border border-border/60 flex items-center justify-center">
              <span className="font-display text-2xl font-bold text-muted-foreground">{companyInitials(company?.name)}</span>
            </div>
          )}
          {!company?.logo_url && (
            <p className="text-xs text-muted-foreground mt-1.5">{t('branding.noLogo', { defaultValue: 'Sin logo — se usa monograma' })}</p>
          )}
          {!readOnly && (
            <div className="flex flex-col gap-1.5 mt-3">
              <Button size="sm" variant="outline" onClick={() => setLogoOpen(true)} data-testid="branding-upload-logo-btn" className="gap-1.5 justify-start">
                <ImagePlus className="h-4 w-4" />
                {company?.logo_url ? t('branding.replaceLogo', { defaultValue: 'Reemplazar logo' }) : t('branding.uploadLogo', { defaultValue: 'Subir logo' })}
              </Button>
              {logoPublished && (
                <Button size="sm" variant="ghost" onClick={doRestoreLogo} disabled={busy} data-testid="branding-restore-logo-btn" className="gap-1.5 justify-start text-muted-foreground">
                  <RotateCcw className="h-3.5 w-3.5" /> {t('branding.restorePreviousLogo', { defaultValue: 'Restaurar logo anterior' })}
                </Button>
              )}
            </div>
          )}
        </div>
        <div className="lg:col-span-2" data-testid="branding-current-banner">
          <div className="flex items-center gap-2 mb-2">
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
              {t('branding.currentBanner', { defaultValue: 'Banner actual' })}
            </p>
            <Badge variant="outline" className="text-[10px]">
              {cb.enabled
                ? t('branding.customBanner', { defaultValue: 'Banner personalizado' })
                : t('branding.defaultHero', { defaultValue: 'Diseño predeterminado CTH' })}
            </Badge>
          </div>
          <HeroPreview
            bannerUrl={cb.enabled ? publishedUrl : null}
            focal={cb.focal_point || 'center'}
            overlay={cb.overlay || 'medium'}
            companyName={company?.name}
            logoUrl={company?.logo_url}
            testId="branding-current-preview"
          />
        </div>
      </div>

      {draft && (
        <div className="mt-5 pt-4 border-t border-border/60" data-testid="branding-draft-block">
          <p className="text-xs uppercase tracking-wider text-primary font-semibold mb-2">
            {t('branding.newBanner', { defaultValue: 'Nuevo banner / Vista previa' })}
          </p>
          <HeroPreview
            bannerUrl={draftUrl}
            focal={draft.focal_point}
            overlay={draft.overlay}
            companyName={company?.name}
            logoUrl={company?.logo_url}
            testId="branding-draft-preview"
          />
          <p className="text-xs text-muted-foreground mt-2 tabular-nums">
            {draft.width} × {draft.height} px · {(draft.size_bytes / 1024).toFixed(0)} KB · {draft.mime}
          </p>
          {!readOnly && (
            <div className="mt-3 space-y-2">
              <SegRow label={t('branding.focal', { defaultValue: 'Punto focal' })} value={draft.focal_point}
                      options={focalOpts} onChange={(v) => setSetting({ focal_point: v })} testPrefix="branding-focal" />
              <SegRow label={t('branding.overlay', { defaultValue: 'Capa de legibilidad' })} value={draft.overlay}
                      options={overlayOpts} onChange={(v) => setSetting({ overlay: v })} testPrefix="branding-overlay" />
              <div className="flex gap-2 pt-2">
                <Button size="sm" onClick={doPublish} disabled={busy} data-testid="branding-publish-btn" className="gap-1.5">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  {busy ? t('branding.publishing', { defaultValue: 'Publicando…' }) : t('branding.publish', { defaultValue: 'Guardar y publicar' })}
                </Button>
                <Button size="sm" variant="outline" onClick={doDiscard} disabled={busy} data-testid="branding-discard-btn" className="gap-1.5">
                  <Trash2 className="h-4 w-4" /> {t('branding.discard', { defaultValue: 'Descartar' })}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {logoDraft && (
        <div className="mt-5 pt-4 border-t border-border/60" data-testid="branding-logo-draft-block">
          <p className="text-xs uppercase tracking-wider text-primary font-semibold mb-2">
            {t('branding.newLogo', { defaultValue: 'Nuevo logo / Vista previa' })}
          </p>
          <div className="flex flex-wrap gap-4 items-start">
            <div className="text-center">
              <div className="h-24 w-24 rounded-xl bg-white border border-border/60 shadow-soft flex items-center justify-center p-2">
                <img src={logoDraftUrl} alt="" className="h-full w-full object-contain" data-testid="branding-logo-draft-light" />
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">{t('branding.logoPreviewLight', { defaultValue: 'Fondo claro' })}</p>
            </div>
            <div className="text-center">
              <div className="h-24 w-24 rounded-xl bg-[#0B1E38] border border-border/60 shadow-soft flex items-center justify-center p-2">
                <img src={logoDraftUrl} alt="" className="h-full w-full object-contain" data-testid="branding-logo-draft-dark" />
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">{t('branding.logoPreviewDark', { defaultValue: 'Fondo oscuro' })}</p>
            </div>
            <div className="text-center">
              <div className="h-24 rounded-xl border border-border/60 bg-background shadow-soft flex items-center gap-2 px-3">
                <div className="h-10 w-10 rounded-lg bg-white border border-border/60 flex items-center justify-center p-1">
                  <img src={logoDraftUrl} alt="" className="h-full w-full object-contain" />
                </div>
                <span className="text-sm font-semibold truncate max-w-[140px]">{company?.name}</span>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">{t('branding.logoPreviewTopbar', { defaultValue: 'Cabecera compacta' })}</p>
            </div>
            <div className="text-center">
              <div className="h-24 w-40 rounded-xl overflow-hidden relative shadow-soft">
                <div className="absolute inset-0 bg-gradient-to-r from-[#0B1E38] via-[#11304F] to-[#1C4B7A]" />
                <div className="relative h-full flex items-center justify-end pr-3">
                  <div className="h-14 w-14 rounded-full bg-white/95 ring-2 ring-white/15 flex items-center justify-center p-1.5">
                    <img src={logoDraftUrl} alt="" className="h-full w-full object-contain" />
                  </div>
                </div>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">{t('branding.logoPreviewHero', { defaultValue: 'Hero del panel' })}</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2 tabular-nums">
            {logoDraft.width} × {logoDraft.height} px · {(logoDraft.size_bytes / 1024).toFixed(0)} KB · {logoDraft.mime}
          </p>
          {!readOnly && (
            <div className="flex gap-2 pt-3">
              <Button size="sm" onClick={doPublishLogo} disabled={busy} data-testid="branding-publish-logo-btn" className="gap-1.5">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                {t('branding.publish', { defaultValue: 'Guardar y publicar' })}
              </Button>
              <Button size="sm" variant="outline" onClick={doDiscardLogo} disabled={busy} data-testid="branding-discard-logo-btn" className="gap-1.5">
                <Trash2 className="h-4 w-4" /> {t('branding.discard', { defaultValue: 'Descartar' })}
              </Button>
            </div>
          )}
        </div>
      )}

      {auditItems.length > 0 && (
        <div className="mt-5 pt-4 border-t border-border/60" data-testid="branding-audit">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-2">
            {t('branding.audit', { defaultValue: 'Historial de cambios' })}
          </p>
          <ul className="space-y-1">
            {auditItems.slice(0, 5).map((a) => (
              <li key={a.id} className="text-xs text-muted-foreground flex flex-wrap gap-x-2">
                <span className="tabular-nums">{new Date(a.at).toLocaleString()}</span>
                <span className="font-medium text-foreground">{t(`branding.actions.${a.action}`, { defaultValue: a.action })}</span>
                <span>· {a.actor_name}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="branding-upload-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ImagePlus className="h-4 w-4 text-primary" />
              {t('branding.upload', { defaultValue: 'Subir banner' })}
            </DialogTitle>
            <DialogDescription>{t('branding.subtitle', { defaultValue: 'Banner del panel del cliente' })}</DialogDescription>
          </DialogHeader>
          {/* Founder §3 — specifications shown IMMEDIATELY, before file selection */}
          <div className="rounded-lg border border-border bg-muted/40 p-4 space-y-1.5 text-sm" data-testid="branding-specs">
            <p className="font-semibold text-foreground">{t('branding.specsTitle', { defaultValue: 'Especificaciones del banner' })}</p>
            <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
              <li data-testid="spec-size">{t('branding.specSize', { defaultValue: 'Recomendado: 1600 × 500 px (alta resolución: 1920 × 600 px)' })}</li>
              <li data-testid="spec-ratio">{t('branding.specRatio', { defaultValue: 'Proporción recomendada: 16:5 (≈ 3.2:1)' })}</li>
              <li data-testid="spec-min">{t('branding.specMin', { defaultValue: 'Resolución mínima: 1280 × 400 px' })}</li>
              <li data-testid="spec-formats">{t('branding.specFormats', { defaultValue: 'Formatos: PNG, JPG/JPEG y WEBP' })}</li>
              <li data-testid="spec-max">{t('branding.specMax', { defaultValue: 'Tamaño máximo: 5 MB' })}</li>
            </ul>
            <p className="text-xs text-muted-foreground pt-1" data-testid="spec-safe-area">
              {t('branding.specSafeArea', { defaultValue: 'Evita incluir texto importante dentro de la imagen.' })}
            </p>
            <p className="text-xs text-muted-foreground" data-testid="spec-crop">
              {t('branding.specCrop', { defaultValue: 'La imagen se adapta tipo cover según el ancho de pantalla.' })}
            </p>
            <p className="text-xs text-muted-foreground italic" data-testid="spec-preview-note">
              {t('branding.specPreview', { defaultValue: 'Antes de publicar podrás previsualizar cómo se verá.' })}
            </p>
          </div>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
                 className="hidden" onChange={onPickFile} data-testid="branding-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="w-full gap-2" data-testid="branding-choose-file-btn">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
            {uploading ? t('branding.uploading', { defaultValue: 'Subiendo…' }) : t('branding.chooseFile', { defaultValue: 'Seleccionar imagen…' })}
          </Button>
        </DialogContent>
      </Dialog>
      <Dialog open={logoOpen} onOpenChange={setLogoOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="branding-logo-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ImagePlus className="h-4 w-4 text-primary" />
              {t('branding.uploadLogo', { defaultValue: 'Subir logo' })}
            </DialogTitle>
            <DialogDescription>{t('branding.logoTitle', { defaultValue: 'Logo del cliente' })}</DialogDescription>
          </DialogHeader>
          {/* Founder §20 — logo specifications shown IMMEDIATELY */}
          <div className="rounded-lg border border-border bg-muted/40 p-4 space-y-1.5 text-sm" data-testid="branding-logo-specs">
            <p className="font-semibold text-foreground">{t('branding.logoSpecsTitle', { defaultValue: 'Especificaciones del logo' })}</p>
            <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
              <li data-testid="logo-spec-square">{t('branding.logoSpecSquare', { defaultValue: 'Imagen cuadrada preferida — recomendado: 512 × 512 px' })}</li>
              <li data-testid="logo-spec-min">{t('branding.logoSpecMin', { defaultValue: 'Resolución mínima: 256 × 256 px' })}</li>
              <li data-testid="logo-spec-formats">{t('branding.logoSpecFormats', { defaultValue: 'Formatos: PNG (preferido), JPG/JPEG y WEBP' })}</li>
              <li data-testid="logo-spec-max">{t('branding.logoSpecMax', { defaultValue: 'Tamaño máximo: 5 MB' })}</li>
            </ul>
            <p className="text-xs text-muted-foreground pt-1" data-testid="logo-spec-usage">
              {t('branding.logoSpecUsage', { defaultValue: 'El logo se muestra en la cabecera compacta, el hero, el organigrama y el perfil del cliente.' })}
            </p>
            <p className="text-xs text-muted-foreground italic">
              {t('branding.specPreview', { defaultValue: 'Antes de publicar podrás previsualizar cómo se verá.' })}
            </p>
          </div>
          <input ref={logoFileRef} type="file" accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
                 className="hidden" onChange={onPickLogo} data-testid="branding-logo-file-input" />
          <Button onClick={() => logoFileRef.current?.click()} disabled={uploading} className="w-full gap-2" data-testid="branding-choose-logo-btn">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
            {uploading ? t('branding.uploading', { defaultValue: 'Subiendo…' }) : t('branding.chooseFile', { defaultValue: 'Seleccionar imagen…' })}
          </Button>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ClientBrandingSection;
